import React, { useCallback, useMemo } from "react";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import invariant from "tiny-invariant";

import { EntityType } from "@mapmycustomers/shared/enum";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import IFiltersConfig, {
  FilterOptionValue,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel, {
  FilterCondition,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import FilterOperatorAction from "@app/types/FilterOperatorAction";
import loggingService from "@app/util/logging";
import { isCombinedCondition } from "@app/util/viewModel/assert";

import commonStyles from "./CommonStyles.module.scss";
import FieldColumn from "./FieldColumn";
import styles from "./FilterSelector.module.scss";
import OperatorColumn from "./OperatorColumn";

interface Props {
  checkInFieldsVisibility: boolean;
  context: string;
  entityType: "universal" | EntityType;
  field: IField;
  fieldModel: IFieldModel;
  filterModel: Partial<FilterModel>;
  filterOperators: FilterOperatorAction;
  filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>;
  firstFilter?: boolean;
  focused?: boolean;
  hiddenFields?: IField[];
  isImplicit?: boolean;
  onChange?: (field: IField, condition: SimpleCondition) => void;
  onRemove?: (field: IField) => void;
  shouldShowField?: (field: IField) => boolean; // whether to include or not this field
  showColumnGroups?: boolean;
}

const FilterSelector: React.FC<Props> = ({
  checkInFieldsVisibility,
  context,
  entityType,
  field,
  fieldModel,
  filterModel,
  filterOperators,
  filterOptions,
  firstFilter,
  focused,
  hiddenFields,
  isImplicit,
  onChange,
  onRemove,
  shouldShowField,
  showColumnGroups,
}) => {
  invariant(field.filter !== false, `Tried to filter by ${field.name} whereas it's not filterable`);

  const filterCondition: FilterCondition | undefined = filterModel[field.filterName];
  loggingService.debug("filterCondition for field", { field, filterCondition });

  invariant(
    !filterCondition || (filterCondition && !isCombinedCondition(filterCondition)),
    `Combined conditions are not yet supported in filter bar`
  );

  const currentOperator = filterCondition?.operator ?? field.filter.defaultOperator;
  const value = filterCondition?.value;

  const instance = useMemo(() => {
    try {
      return (field.filter as IFiltersConfig).filterInstances[
        (field.filter as IFiltersConfig).operators.find(
          ({ operator }) => operator === currentOperator
        )!.instance
      ];
    } catch (error) {
      loggingService.error(`Failed to get instance for the given field and operator`, {
        currentOperator,
        fieldFilters: field.filter,
        fieldName: field.name,
        fieldType: field.type,
      });
      throw error;
    }
  }, [currentOperator, field]);

  const handleFieldChange = useCallback(
    (oldField, newField, newCondition) => {
      onRemove?.(oldField);
      onChange?.(newField, newCondition);
    },
    [onRemove, onChange]
  );

  const handleOperatorChange = useCallback(
    (newCondition: SimpleCondition) => {
      onChange?.(field, newCondition);
    },
    [field, onChange]
  );

  const handleValueChange = useCallback(
    (filterCondition: FilterCondition) => {
      if (isCombinedCondition(filterCondition)) {
        return; // should not happen
      }
      onChange?.(field, { operator: filterCondition.operator, value: filterCondition.value });
    },
    [field, onChange]
  );

  const InstanceComponent = useMemo(() => instance.instance.getComponent(), [instance]);

  const handleRemoveIconClick = useCallback(() => {
    onRemove?.(field);
  }, [field, onRemove]);

  const instanceOptions = useMemo(
    () => ({ ...filterOptions, ...instance.options }),
    [filterOptions, instance.options]
  );

  // InstanceComponent is re-rendered for no reason without this useMemo
  const filterValue = useMemo(
    () => ({ operator: currentOperator, value }),
    [currentOperator, value]
  );

  return (
    <>
      <FieldColumn
        checkInFieldsVisibility={checkInFieldsVisibility}
        className={styles.fieldName}
        context={context}
        disabled={isImplicit}
        entityType={entityType}
        field={field}
        fieldModel={fieldModel}
        filterModel={filterModel}
        filterOperators={filterOperators}
        firstFilter={firstFilter}
        focused={focused}
        hiddenFields={hiddenFields}
        onChange={handleFieldChange}
        shouldShowField={shouldShowField}
        showColumnGroups={showColumnGroups}
      />
      {field.hasFeature(FieldFeature.VIEW_AS_FIELD) ? null : (
        <OperatorColumn
          className={cn(styles.operator, { [commonStyles.focusSplash]: focused })}
          disabled={isImplicit}
          field={field}
          filterModel={filterModel}
          filterOperators={filterOperators}
          onChange={handleOperatorChange}
        />
      )}
      <div
        className={cn(styles.component, {
          [commonStyles.focusSplash]: focused,
          [styles.largeComponent]: field.hasFeature(FieldFeature.VIEW_AS_FIELD),
        })}
      >
        <InstanceComponent
          className={styles.instanceComponent}
          field={field}
          filterModel={filterModel}
          focus={focused}
          onChange={handleValueChange}
          options={instanceOptions}
          value={filterValue}
        />
      </div>
      <div className={styles.actions}>
        {!isImplicit && (
          <FontAwesomeIcon
            className={styles.actionIcon}
            icon={faMinusCircle}
            onClick={handleRemoveIconClick}
          />
        )}
      </div>
    </>
  );
};

export default FilterSelector;
